import React from 'react';
import { ArrowLeft, Calendar, Navigation, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { EventPost } from '@feature/event-detail/types';
import { formatEventTime } from '@feature/event-detail/utils/formatEventTime';

interface Props {
  event: EventPost;
}

export function FullEventDetailScreen({ event }: Props): React.ReactElement {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  const handleDirectionsPress = () => {
    if (event.latitude && event.longitude) {
      const url = `https://www.google.com/maps/search/?api=1&query=${event.latitude},${event.longitude}`;
      window.open(url, '_blank');
    }
  };

  const handleSharePress = () => {
    // TODO: Implement share functionality
    console.log('Share event:', event.id);
  };

  const timeDisplay = formatEventTime(event.starts_at, event.ends_at);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button
          style={styles.backButton}
          onClick={handleBack}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f0f0f0';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <ArrowLeft size={24} color="#333" />
        </button>
      </div>

      <div style={styles.scrollView}>
        <div style={styles.scrollContent}>
          {/* Title */}
          <h1 style={styles.title}>{event.title}</h1>

          {/* Time */}
          <div style={styles.timeContainer}>
            <Calendar size={18} color="#3b82f6" />
            <span style={styles.timeText}>{timeDisplay}</span>
          </div>

          {/* Action Buttons */}
          <div style={styles.actionsRow}>
            {event.latitude && event.longitude && (
              <button style={styles.actionButton} onClick={handleDirectionsPress}>
                <span style={styles.actionButtonText}>Directions</span>
                <Navigation size={16} color="#3b82f6" />
              </button>
            )}
            <button style={styles.actionButton} onClick={handleSharePress}>
              <span style={styles.actionButtonText}>Share</span>
              <Share2 size={16} color="#3b82f6" />
            </button>
          </div>

          {/* Photo */}
          {event.photo_url_compressed && (
            <div style={styles.photoContainer}>
              <img
                src={event.photo_url_compressed}
                alt={event.title}
                style={styles.photo}
              />
            </div>
          )}

          {/* Description */}
          {event.message_text && (
            <div style={styles.descriptionSection}>
              <h3 style={styles.sectionLabel}>DESCRIPTION</h3>
              <p style={styles.description}>{event.message_text}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: '#fff',
    height: 'calc(var(--vh, 1vh) * 100)',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '1000px',
    margin: '0 auto',
  },
  header: {
    borderBottom: '1px solid #e0e0e0',
    padding: '8px 16px',
  },
  backButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    padding: '8px',
    borderRadius: '8px',
    transition: 'background-color 0.2s',
  },
  scrollView: {
    flex: 1,
    overflowY: 'auto',
  },
  scrollContent: {
    padding: '20px',
  },
  title: {
    color: '#000',
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '16px',
    margin: 0,
  },
  timeContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
  },
  timeText: {
    color: '#666',
    fontSize: '16px',
  },
  actionsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#f0f9ff',
    borderRadius: '8px',
    gap: '6px',
    padding: '8px 16px',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  actionButtonText: {
    color: '#3b82f6',
    fontSize: '14px',
    fontWeight: '600',
  },
  photoContainer: {
    borderRadius: '12px',
    marginBottom: '24px',
    overflow: 'hidden',
  },
  photo: {
    aspectRatio: '16 / 9',
    width: '100%',
    display: 'block',
  },
  descriptionSection: {
    marginBottom: '24px',
  },
  sectionLabel: {
    color: '#999',
    fontSize: '10px',
    fontWeight: '600',
    letterSpacing: '0.5px',
    marginBottom: '12px',
    textTransform: 'uppercase',
    margin: '0 0 12px 0',
  },
  description: {
    color: '#000',
    fontSize: '16px',
    lineHeight: '32px',
    margin: 0,
    whiteSpace: 'pre-wrap',
  },
};
